/**
 * Google Analytics 4 (gtag.js) — page views + conversion events.
 *
 * Design notes:
 *  * Measurement ID comes from REACT_APP_GA_MEASUREMENT_ID — analytics is a
 *    silent no-op everywhere when the env var is missing (e.g. local dev).
 *  * The gtag script is injected lazily by initGA() so index.html stays
 *    untouched and no request is made unless an ID is configured.
 *  * GDPR / UK PECR: we run Google Consent Mode v2. analytics_storage
 *    defaults to DENIED; it is granted only after the visitor accepts the
 *    existing cookie banner (same localStorage flag the banner uses).
 *    While denied, GA4 still receives cookieless consent-mode pings, so
 *    page views and sign-up conversions are still modelled without
 *    setting any analytics cookies.
 *  * SPA page views: gtag is configured with send_page_view:false and the
 *    <AnalyticsTracker /> component fires page_view on every route change
 *    (including the first load) — no double counting.
 */

const GA_ID = process.env.REACT_APP_GA_MEASUREMENT_ID;

// MUST match the key written by CookieBanner.js — one consent flag app-wide.
export const CONSENT_LS_KEY = 'rotahq_cookie_consent_v1';

let initialised = false;

function gtag() {
  if (typeof window === 'undefined') return;
  window.dataLayer = window.dataLayer || [];
  // CRITICAL: gtag.js only processes `arguments` objects pushed onto the
  // dataLayer — a plain array (e.g. from a rest parameter) is silently
  // ignored and no hit is ever dispatched. Do NOT "modernise" this.
  // eslint-disable-next-line prefer-rest-params
  window.dataLayer.push(arguments);
}

export function hasAnalyticsConsent() {
  try {
    return !!localStorage.getItem(CONSENT_LS_KEY);
  } catch {
    return false;
  }
}

export function initGA() {
  if (initialised || !GA_ID || typeof window === 'undefined') return;
  initialised = true;

  window.dataLayer = window.dataLayer || [];
  window.gtag = window.gtag || gtag;

  // Consent Mode v2 defaults MUST be set before the config call.
  gtag('consent', 'default', {
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    analytics_storage: hasAnalyticsConsent() ? 'granted' : 'denied',
  });
  gtag('js', new Date());
  // send_page_view:false — AnalyticsTracker owns ALL page_view events so
  // SPA route changes and the initial load are counted exactly once each.
  gtag('config', GA_ID, { send_page_view: false, anonymize_ip: true });

  const s = document.createElement('script');
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  document.head.appendChild(s);
}

/** Called by the cookie banner the moment the visitor accepts. */
export function grantAnalyticsConsent() {
  if (!GA_ID) return;
  gtag('consent', 'update', { analytics_storage: 'granted' });
}

let lastTrackedPath = null;

export function trackPageView(path, title) {
  if (!GA_ID) return;
  // Consecutive-duplicate guard: React StrictMode double-runs effects in
  // dev, which would fire the initial page_view twice. A→B→A sequences
  // still track every step because only back-to-back repeats are skipped.
  if (path === lastTrackedPath) return;
  lastTrackedPath = path;
  gtag('event', 'page_view', {
    page_path: path,
    page_location: window.location.href,
    page_title: title || document.title,
  });
}

export function trackEvent(name, params = {}) {
  if (!GA_ID) return;
  gtag('event', name, params);
}

/**
 * Fire an event, then run `done` — used before HARD navigations (e.g. the
 * signup success redirect) where an in-flight hit would otherwise be lost.
 * Proceeds via event_callback when gtag confirms dispatch, with a 500ms
 * fallback so a blocked tracker can never delay the user.
 */
export function trackEventThenRun(name, params, done) {
  if (!GA_ID || typeof window.gtag !== 'function') {
    done();
    return;
  }
  let called = false;
  const proceed = () => {
    if (called) return;
    called = true;
    done();
  };
  gtag('event', name, { ...params, event_callback: proceed });
  setTimeout(proceed, 500);
}
